import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Table } from 'antd';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import useApiClient from '../../hooks/api/useApiClient';
import useHelpers from '../../hooks/context/useHelpers';
import { ManagerRole } from '../../hooks/auth/usePermissions';

const sorterFields = {
  total_orders: 'total_orders',
  total_sales: 'total_sales',
  total_payments: 'total_payments',
  payments_ratio: 'payments_ratio',
};

const sorterDirections = {
  ascend: 'ascend',
  descend: 'descend',
};

export default function CompaniesPreviewModal({ open, onClose, companyIds, period, additionalParams }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:CustomerBase:customer_health_tab' });

  const { CustomerBaseStatsManager } = useApiClient();

  const { formatCurrency } = useHelpers();

  const { profile } = useSelector((state) => state.initialData);

  const isSalesManager = profile.roles.includes(ManagerRole.SalesManager);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sorter, setSorter] = useState({
    field: sorterFields.total_sales,
    order: sorterDirections.descend,
  });

  useEffect(() => {
    open && fetchData(data?.current_page || 1, sorter);
  }, [open]);

  const fetchData = async (page, sorter) => {
    setLoading(true);
    const { data, success } = await CustomerBaseStatsManager.getPerformanceStats({
      start_date: moment(period).startOf('month').utc().format(),
      end_date: moment(period).endOf('month').utc().format(),
      page,
      sort_field: sorter.field,
      sort_order: sorter.order === sorterDirections.ascend ? 'asc' : 'desc',
      managers: isSalesManager ? [profile.id] : [],
      company_ids: companyIds,
      include_payments_info: additionalParams?.with_payments_info,
    });
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    setData(data);
  };

  const onCloseHandle = () => {
    setData(null);
    onClose();
  };

  const handleTableChange = (pagination, filters, pendingSorter) => {
    const newSorter = {
      field: pendingSorter.field || sorter.field,
      order:
        pendingSorter.order ||
        (sorter.order === sorterDirections.descend ? sorterDirections.ascend : sorterDirections.descend),
    };

    setSorter(newSorter);

    fetchData(pagination.current, newSorter);
  };

  const columns = [
    {
      title: t('companies_preview_modal.table_columns.name'),
      key: 'name',
      dataIndex: 'name',
      visible: true,
    },
    {
      title: t('companies_preview_modal.table_columns.sales_manager'),
      key: 'sales_manager_full_name',
      dataIndex: 'sales_manager_full_name',
      visible: true,
    },
    {
      title: t('companies_preview_modal.table_columns.orders'),
      key: 'total_orders',
      dataIndex: 'total_orders',
      sorter: true,
      sortOrder: sorter.field === sorterFields.total_orders ? sorter.order : null,
      visible: true,
    },
    {
      title: t('companies_preview_modal.table_columns.sales'),
      key: 'total_sales',
      dataIndex: 'total_sales',
      sorter: true,
      sortOrder: sorter.field === sorterFields.total_sales ? sorter.order : null,
      render: (value) => formatCurrency(value),
      visible: true,
    },
    {
      title: t('companies_preview_modal.table_columns.total_payments'),
      key: 'total_payments',
      dataIndex: 'total_payments',
      sorter: true,
      sortOrder: sorter.field === sorterFields.total_payments ? sorter.order : null,
      render: (value) => formatCurrency(value),
      visible: additionalParams?.with_payments_info,
    },
    {
      title: t('companies_preview_modal.table_columns.payments_ratio'),
      key: 'payments_ratio',
      dataIndex: 'payments_ratio',
      sorter: true,
      sortOrder: sorter.field === sorterFields.payments_ratio ? sorter.order : null,
      visible: additionalParams?.with_payments_info,
    },
  ].filter((column) => column.visible);

  return (
    <Modal
      title={t('companies_preview_modal.title')}
      footer={
        <Button type="default" onClick={onCloseHandle}>
          {t('companies_preview_modal.labels.close')}
        </Button>
      }
      open={open}
      onCancel={onCloseHandle}
      width={1000}
      style={{ top: 60 }}
    >
      <Table
        dataSource={data?.data}
        columns={columns}
        pagination={{
          current: data?.current_page,
          total: data?.total,
          pageSize: data?.per_page,
          className: 'pr-[5px]',
        }}
        sorter={sorter}
        loading={loading}
        onChange={handleTableChange}
        className="rounded-xl shadow-card bg-white overflow-hidden"
        rowKey={(item) => item.id}
        locale={{
          emptyText: t('companies_preview_modal.labels.no_data'),
          triggerDesc: t('companies_preview_modal.labels.click_to_sort'),
          triggerAsc: t('companies_preview_modal.labels.click_to_sort'),
          cancelSort: t('companies_preview_modal.labels.click_to_sort'),
        }}
      />
    </Modal>
  );
}
